import { Plus, Ticket, TrendingUp, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';

import { useFeatureAccess } from '@/features/billing/model/use-feature-access';
import { ProFeatureLock } from '@/features/billing/ui/pro-feature-lock';
import { useCafe24Coupons, useCouponEvents } from '@/features/coupon';
import { testIds } from '@/shared/testing';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import { DataTablePagination } from '@/shared/ui/data-table-pagination';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { PageHeader } from '@/shared/ui/page-header';
import { Skeleton } from '@/shared/ui/skeleton';
import { SortIcon } from '@/shared/ui/sort-icon';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { AnimatedContainer } from '@/shared/ui/animated';
import { AppLayout } from '../-components/app-layout';

import type {
  CouponEventSortBy,
  CouponEventWithBatchSummaryDto,
  CouponIssueBatchStatus,
  SortOrder,
} from '@cafe24-apps/traffic-light-server/contracts';

export const Route = createFileRoute('/_authenticated/$mallId/coupon/')({
  component: CouponListPage,
});

const BATCH_STATUS_CONFIG: Record<
  CouponIssueBatchStatus,
  {
    label: string;
    variant: 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning';
  }
> = {
  issuing: { label: '발급중', variant: 'secondary' },
  completed: { label: '완료', variant: 'success' },
  partial_failed: { label: '부분실패', variant: 'warning' },
  failed: { label: '실패', variant: 'destructive' },
};

function BatchStatusBadge({ status }: { status: CouponIssueBatchStatus | null }) {
  if (!status) {
    return <Badge variant="outline">미발급</Badge>;
  }
  const config = BATCH_STATUS_CONFIG[status];
  return <Badge variant={config.variant}>{config.label}</Badge>;
}

function CouponListPage() {
  const { mallId } = useParams({ from: '/_authenticated/$mallId/coupon/' });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [couponFilter, setCouponFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<CouponEventSortBy>('createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const { locked } = useFeatureAccess('couponAutoIssueEnabled');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading } = useCouponEvents({
    page,
    limit,
    search: debouncedSearch || undefined,
    cafe24CouponNo: couponFilter === 'all' ? undefined : couponFilter,
    sortBy,
    sortOrder,
    enabled: !locked,
  });

  const { data: couponsData } = useCafe24Coupons({ enabled: !locked });
  const cafe24Coupons = couponsData?.data ?? [];

  const events = data?.data ?? [];
  const pagination = data?.pagination;
  const totalPages = pagination ? Math.ceil(pagination.total / pagination.limit) : 1;

  const handleSort = (field: CouponEventSortBy) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const handleLimitChange = (value: number) => {
    setLimit(value);
    setPage(1);
  };

  const hasFilters = debouncedSearch || couponFilter !== 'all';

  return (
    <AppLayout pageTitle="쿠폰">
      <ProFeatureLock feature="couponAutoIssueEnabled">
        <div className="space-y-6 max-w-5xl mx-auto">
          {/* 헤더 */}
          <AnimatedContainer delay={0}>
            <PageHeader
              title="쿠폰"
              description="이벤트 단위로 고객에게 쿠폰을 발급하고 결과를 확인합니다"
              action={
                <Button asChild className="gap-1.5" data-testid={testIds.coupon.createButton}>
                  <Link to="/$mallId/coupon/new" params={{ mallId: mallId }}>
                    <Plus className="h-4 w-4" />새 이벤트
                  </Link>
                </Button>
              }
            />
          </AnimatedContainer>

          {/* 요약 카드 */}
          {pagination && data?.summary && (
            <AnimatedContainer delay={0.1}>
              <SummaryCards summary={data.summary} total={pagination.total} />
            </AnimatedContainer>
          )}

          {/* 테이블 */}
          <AnimatedContainer delay={0.2}>
            <Card className="py-0 gap-0">
              {/* 액션바: 검색 + 필터 */}
              <div className="flex flex-col gap-3 px-6 py-4 border-b sm:flex-row sm:items-center sm:justify-between">
                <Input
                  placeholder="이벤트명 검색..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-8 w-full sm:w-52 text-sm"
                  data-testid={testIds.coupon.searchInput}
                />
                <div className="flex items-center gap-2">
                  <Select
                    value={couponFilter}
                    onValueChange={(v) => {
                      setCouponFilter(v);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger
                      size="sm"
                      className="h-8 w-[140px] text-xs"
                      data-testid={testIds.coupon.couponFilter}
                    >
                      <SelectValue placeholder="쿠폰" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" className="text-xs">
                        전체 쿠폰
                      </SelectItem>
                      {cafe24Coupons.map((c) => (
                        <SelectItem key={c.coupon_no} value={c.coupon_no} className="text-xs">
                          {c.coupon_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>이벤트명</TableHead>
                    <TableHead>쿠폰</TableHead>
                    <TableHead>발급 횟수</TableHead>
                    <TableHead>총 발급</TableHead>
                    <TableHead>최신 상태</TableHead>
                    <TableHead>
                      <button
                        type="button"
                        className="flex items-center hover:text-foreground transition-colors"
                        onClick={() => handleSort('createdAt')}
                      >
                        생성일
                        <SortIcon column="createdAt" sortBy={sortBy} sortOrder={sortOrder} />
                      </button>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          {Array.from({ length: 6 }).map((_, j) => (
                            <TableCell key={j}>
                              <Skeleton className="h-4 w-full" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    : events.map((event) => (
                        <EventRow key={event.id} event={event} mallId={mallId} />
                      ))}
                  {!isLoading && events.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12">
                        <div className="flex flex-col items-center gap-3">
                          <div
                            className="rounded-full bg-muted p-3"
                            data-testid={testIds.coupon.emptyMessage}
                          >
                            <Ticket className="h-6 w-6 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">
                              {hasFilters
                                ? '조건에 해당하는 이벤트가 없습니다'
                                : '아직 쿠폰 이벤트가 없습니다'}
                            </p>
                            {!hasFilters && (
                              <p className="text-xs text-muted-foreground mt-1">
                                첫 번째 이벤트를 만들어 고객에게 쿠폰을 발급해보세요
                              </p>
                            )}
                          </div>
                          {!hasFilters && (
                            <Button size="sm" asChild className="gap-1.5 mt-1">
                              <Link to="/$mallId/coupon/new" params={{ mallId: mallId }}>
                                <Plus className="h-4 w-4" />새 이벤트
                              </Link>
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {/* 푸터 — 페이지네이션 */}
              {pagination && (
                <DataTablePagination
                  page={page}
                  totalPages={totalPages}
                  total={pagination.total}
                  limit={limit}
                  onPageChange={setPage}
                  onLimitChange={handleLimitChange}
                />
              )}
            </Card>
          </AnimatedContainer>
        </div>
      </ProFeatureLock>
    </AppLayout>
  );
}

function SummaryCards({
  summary,
  total,
}: {
  summary: { totalIssued: number; totalSuccess: number; totalFailed: number };
  total: number;
}) {
  const { totalIssued, totalSuccess } = summary;
  const successRate = totalIssued > 0 ? Math.round((totalSuccess / totalIssued) * 100) : 0;

  return (
    <div className="grid grid-cols-3 gap-4">
      <Card className="py-4">
        <CardContent className="flex items-center gap-3">
          <div className="rounded-lg bg-primary/10 p-2">
            <Ticket className="h-4 w-4 text-primary" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">전체 이벤트</p>
            <p className="text-2xl font-semibold">{total}</p>
          </div>
        </CardContent>
      </Card>
      <Card className="py-4">
        <CardContent className="flex items-center gap-3">
          <div className="rounded-lg bg-primary/10 p-2">
            <Users className="h-4 w-4 text-primary" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">총 발급 수</p>
            <p className="text-2xl font-semibold">{totalIssued.toLocaleString()}건</p>
          </div>
        </CardContent>
      </Card>
      <Card className="py-4">
        <CardContent className="flex items-center gap-3">
          <div className="rounded-lg bg-signal-green/10 p-2">
            <TrendingUp className="h-4 w-4 text-signal-green" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">성공률</p>
            <p className="text-2xl font-semibold">{successRate}%</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function EventRow({ event, mallId }: { event: CouponEventWithBatchSummaryDto; mallId: string }) {
  const navigate = useNavigate();

  const handleRowClick = () => {
    navigate({ to: '/$mallId/coupon/$eventId', params: { mallId, eventId: event.id } });
  };

  return (
    <TableRow
      className="cursor-pointer hover:bg-accent/50"
      onClick={handleRowClick}
      data-testid={testIds.coupon.eventRow}
    >
      <TableCell className="font-medium">{event.name}</TableCell>
      <TableCell className="text-muted-foreground">{event.cafe24CouponName}</TableCell>
      <TableCell>
        <span className="tabular-nums">{event.batchCount}</span>회
      </TableCell>
      <TableCell>
        <span className="tabular-nums">
          {event.totalSuccess}/{event.totalIssued}
        </span>
      </TableCell>
      <TableCell>
        <BatchStatusBadge status={event.latestBatchStatus} />
      </TableCell>
      <TableCell className="text-muted-foreground tabular-nums">
        {new Date(event.createdAt).toLocaleDateString('ko-KR')}
      </TableCell>
    </TableRow>
  );
}
